import React, { ReactElement } from 'react'
import { Image, ThemeUIStyleObject } from 'theme-ui'
import { BASE_URL_ASSETS, CONTROL_D_DOMAIN } from 'gatsby-env-variables'
import useGetColorMode from 'utils/useGetColorMode'
import { useLocation } from '@reach/router'

export enum IconType {
  FILTERS = 'filters',
  SERVICES = 'services',
  LOCATIONS = 'locations',
  PROFILE_OPTIONS = 'profile-options',
}

export const globeIcon = 'globe'

export interface ExternalIcon {
  icon: string
  type: IconType
}

// we can change external svg color only using css filter
export const colorFilters = {
  grayscaleLightMode: 'grayscale(1)',
  grayscaleLightMode60: 'grayscale(1) opacity(60%)',
  grayscale: 'grayscale(1) invert(1)',
  white: 'brightness(0) invert(1)',
  white50: 'brightness(0) invert(1) opacity(50%)',
  black: 'brightness(0) saturate(100%)',
  successOpaque:
    'brightness(0) saturate(100%) invert(72%) sepia(21%) saturate(2267%) hue-rotate(109deg) brightness(105%) contrast(79%)',
  lightSteelBlue:
    'brightness(0) saturate(100%) invert(84%) sepia(2%) saturate(5536%) hue-rotate(191deg) brightness(90%) contrast(88%)',
  greenApple:
    'brightness(0) saturate(100%) invert(83%) sepia(55%) saturate(5167%) hue-rotate(49deg) brightness(100%) contrast(106%)',
  aliceBlue:
    'brightness(0) saturate(100%) invert(89%) sepia(79%) saturate(4674%) hue-rotate(179deg) brightness(104%) contrast(110%)',
  aliceBlue60:
    'brightness(0) saturate(100%) invert(89%) sepia(79%) saturate(4674%) hue-rotate(179deg) brightness(104%) contrast(110%) opacity(60%)',
}

export default function ExternalSvgIcon({
  className,
  descriptiveText,
  icon,
  type,
  sx,
  fill,
  testId,
  defaultColor,
  ignoreDefaultLightModeFilter,
}: {
  className?: string
  descriptiveText: string
  icon: string
  type: IconType
  sx?: ThemeUIStyleObject
  fill?: string
  testId?: string
  defaultColor?: string
  ignoreDefaultLightModeFilter?: boolean
}): ReactElement | null {
  const { isLightMode } = useGetColorMode()
  const location = useLocation()
  if (!icon) {
    return null
  }

  return (
    <Image
      /**
       * The cross origin attribute is used to specify the CORS policy for the image so that it is cached properly and
       * is not treated as an opaque object which costs 7mb of memory per image.
       * https://developer.chrome.com/docs/workbox/caching-resources-during-runtime#remember_to_opt_into_cors_mode
       * We need to apply cross origin only for controld.com and controld.dev, but not for localhost or any other branch.
       */
      crossOrigin={location.origin === `https://${CONTROL_D_DOMAIN}` ? 'anonymous' : undefined}
      alt={descriptiveText}
      aria-label={descriptiveText}
      title={descriptiveText}
      data-testid={testId}
      className={className}
      src={
        type === IconType.LOCATIONS
          ? `${BASE_URL_ASSETS}/icons/flags/round/svg/${icon}.svg`
          : type === IconType.PROFILE_OPTIONS
          ? `${BASE_URL_ASSETS}/icons/profile-options/svg/${icon}.svg`
          : `${BASE_URL_ASSETS}/icons/${type}/svg/${
              icon.startsWith('x-') ? 'third-party' : icon
            }.svg`
      }
      sx={{
        ...(fill &&
          (isLightMode && !ignoreDefaultLightModeFilter
            ? {
                filter: fill.includes('60')
                  ? colorFilters.grayscaleLightMode60
                  : colorFilters.grayscaleLightMode,
              }
            : { filter: colorFilters[fill] })),
        ...((icon === '?' || icon === 'N/A') && !isLightMode && !fill
          ? { filter: colorFilters.white }
          : {}),
        ...sx,
      }}
      onError={({ currentTarget }) => {
        currentTarget.onerror = null
        if (currentTarget.getAttribute('crossorigin')) {
          currentTarget.removeAttribute('crossorigin')
        }
        const url =
          type === IconType.LOCATIONS
            ? `${BASE_URL_ASSETS}/icons/flags/round/svg/globe.svg`
            : `${BASE_URL_ASSETS}/icons/general/svg/placeholder.svg`
        // cache-bust to avoid 304/opaque hits
        currentTarget.src = `${url}?v=1`

        if (defaultColor) {
          currentTarget.style.filter = colorFilters[defaultColor]
        }
      }}
    />
  )
}
